import { useEffect, useMemo, useRef, useState } from 'react';
import { exitPresentation, presentFrame, presentStep, presentationFrames } from '../actions/actions';
import type { BoardDoc } from '../collab/BoardDoc';
import { useDocVersion } from '../collab/hooks';
import { useEditor } from '../state/store';
import { Icon } from './icons';

/**
 * Presentation player chrome.
 *
 * Presenting does not swap in a separate renderer: it reuses the live
 * `BoardCanvas` and just points the camera at one frame at a time, with the
 * editing chrome hidden. That is what keeps the board "fully interactive"
 * while presenting — annotations, the laser pointer and peer cursors keep
 * working exactly as they do outside presentation mode.
 */
export function PresentationBar({ doc }: { doc: BoardDoc }) {
  const presentingFrameId = useEditor((s) => s.presentingFrameId);
  const tool = useEditor((s) => s.tool);
  const version = useDocVersion(doc);
  const hostRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setFullscreen] = useState(false);

  const frames = useMemo(() => presentationFrames(doc), [doc, version]);
  const index = frames.findIndex((f) => f.id === presentingFrameId);

  // Keep the player pointed at a real frame if the current one was deleted or
  // reordered out from under it, instead of freezing on a dead id.
  useEffect(() => {
    if (presentingFrameId === null) return;
    if (index === -1 && frames.length > 0) presentFrame(doc, frames[0]!.id, { immediate: true });
  }, [doc, presentingFrameId, index, frames]);

  useEffect(() => {
    if (presentingFrameId === null) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable)) {
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
        exitPresentation();
        return;
      }
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        presentStep(doc, 1);
        return;
      }
      if (e.key === 'ArrowLeft' || e.key === 'Backspace' || e.key === 'PageUp') {
        e.preventDefault();
        presentStep(doc, -1);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [doc, presentingFrameId]);

  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement != null);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  if (presentingFrameId === null) return null;

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => undefined);
    } else {
      void hostRef.current?.closest('.board-area')?.requestFullscreen?.().catch(() => undefined);
    }
  };

  const current = frames[index] ?? null;

  return (
    <div className="presentation-bar" ref={hostRef} role="toolbar" aria-label="Показ презентации">
      <button
        type="button"
        className="icon-btn"
        onClick={() => {
          if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
          exitPresentation();
        }}
        aria-label="Завершить показ (Esc)"
        data-tip="Завершить показ · Esc"
      >
        <Icon name="close" size={16} />
      </button>

      <button
        type="button"
        className="icon-btn"
        onClick={() => presentStep(doc, -1)}
        disabled={frames.length < 2}
        aria-label="Предыдущий слайд"
        data-tip="Предыдущий слайд · ←"
      >
        <Icon name="arrowLeft" size={16} />
      </button>

      <span className="presentation-counter">
        {current ? current.name || 'Фрейм' : '—'}
        <span className="presentation-counter-n">
          {frames.length > 0 ? `${index + 1} / ${frames.length}` : ''}
        </span>
      </span>

      <button
        type="button"
        className="icon-btn"
        onClick={() => presentStep(doc, 1)}
        disabled={frames.length < 2}
        aria-label="Следующий слайд"
        data-tip="Следующий слайд · →"
      >
        <Icon name="arrowLeft" size={16} className="icon-flip" />
      </button>

      <div className="toolbar-sep" />

      <button
        type="button"
        className={`icon-btn ${tool === 'laser' ? 'is-active' : ''}`}
        onClick={() => useEditor.getState().setTool(tool === 'laser' ? 'select' : 'laser', { lock: true })}
        aria-label="Лазерная указка"
        data-tip="Лазерная указка · K"
      >
        <Icon name="laser" size={16} />
      </button>

      <button
        type="button"
        className="icon-btn"
        onClick={toggleFullscreen}
        aria-label={isFullscreen ? 'Выйти из полноэкранного режима' : 'На весь экран'}
        data-tip={isFullscreen ? 'Выйти из полноэкранного режима' : 'На весь экран'}
      >
        <Icon name={isFullscreen ? 'minimize' : 'maximize'} size={16} />
      </button>
    </div>
  );
}
